import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  OnboadingStepNames,
  OnboardingStep,
  OnboardingStepStatus,
  Organization,
  QualityPermissions,
} from '@app/models';
import {
  FirstStepsService,
  OnboardingStepService,
  OrganizationService,
  PermissionService,
  Products,
  SystemMessages,
  ToastService,
  UserService,
  WebsocketService,
} from '@app/services';
import { UserInterface } from 'app/_models/interfaces/user.interface';
import { NgxPermissionsService } from 'ngx-permissions';
import { environment } from '../../../environments/environment';
import { CacheInterceptorService } from '../../_services/cache-interceptor.service';
import { BusinessException } from 'app/_helpers/business-exception.enum';

@Component({
  selector: 'app-organizations',
  templateUrl: './organizations.component.html',
  styleUrls: ['./organizations.component.scss'],
})
export class OrganizationsComponent implements OnInit {
  public organizations: Organization[] = [];
  public currentUser: UserInterface;

  public switchOrganization: string;

  public showOrganizationList: boolean = false;

  constructor(
    private organizationService: OrganizationService,
    private userService: UserService,
    private permissionService: PermissionService,
    private toastService: ToastService,
    private permissionsService: NgxPermissionsService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private onboardingStepService: OnboardingStepService,
    private firstStepsService: FirstStepsService,
    private onboardingService: OnboardingStepService,
    private cacheInterceptorService: CacheInterceptorService,
    private websocketService: WebsocketService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  ngOnInit(): void {
    this.currentUser = this.userService.currentUser;

    if (
      localStorage.getItem('organizationId') &&
      !localStorage.getItem('switchOrganization')
    ) {
      this.organizationService.currentOrganization = new Organization();
      this.organizationService.currentOrganization.id =
        localStorage.getItem('organizationId');
      this.organizationService.currentOrganization.alias =
        localStorage.getItem('organizationAlias');

      this.getOrganization(localStorage.getItem('organizationId'));
      return null;
    }
    this.getOrganizations();
  }

  public getOrganization(id: string): void {
    this.organizationService.find(id).subscribe(
      data => {
        this.selectOrganization(data);
      },
      error => {
        console.error(error);
        if (
          (error?.error?.error != null || error?.error?.error != undefined) &&
          BusinessException[error?.error?.error] != null
        ) {
          this.toastService.showWarning(BusinessException[error?.error?.error]);
        } else {
          this.toastService.showDanger(SystemMessages.UNEXPECTED_ERROR);
        }
      }
    );
  }

  public getOrganizations(): void {
    this.organizationService.findAll().subscribe(
      data => {
        if (localStorage.getItem('switchOrganization')) {
          this.selectOrganization(
            data.find(
              item => item.alias == localStorage.getItem('switchOrganization')
            )
          );
          localStorage.removeItem('switchOrganization');

          return;
        }

        if (
          data.length == 1 &&
          !this.organizationService.forceOrganizationSelection
        ) {
          this.selectOrganization(data[0]);
          return;
        }

        if (data.length == 0) {
          this.document.location.href = environment.apps['admin'];
          return;
        }

        this.organizationService.forceOrganizationSelection = false;
        this.showOrganizationList = true;
        // filter data status == 'ACTIVE'
        this.organizations = data.filter(item => item.status === 'ACTIVE');
      },
      error => {
        console.error(error);
        if (
          (error?.error?.error != null || error?.error?.error != undefined) &&
          BusinessException[error?.error?.error] != null
        ) {
          this.toastService.showWarning(BusinessException[error?.error?.error]);
        } else {
          this.toastService.showDanger(SystemMessages.UNEXPECTED_ERROR);
        }
      }
    );
  }

  public selectOrganization(organization: Organization): void {
    this.organizationService.currentOrganization = organization;

    if (!this.organizationService.currentOrganization.plan) {
      this.organizationService.currentOrganization.plan = {
        id: '',
        name: '',
        type: 'FREE',
        status: '',
        automaticChecklist: false,
        availableDashboards: [],
        interactionsPerMonth: 0,
        training: false,
      };
    }

    localStorage.setItem('organizationId', organization.id);
    localStorage.setItem('organizationAlias', organization.alias);
    localStorage.setItem('organizationName', organization.name);

    if (this.userService.returnUrl == '/organizations') {
      this.userService.returnUrl = '/';
    }

    this.getCurrentUserPermissions();
  }

  private getCurrentUserPermissions(): void {
    this.userService.getCurrentUserPermissions().subscribe(
      permissionsApps => {
        let allPermissions = [];

        permissionsApps.forEach(profile => {
          allPermissions = [
            ...allPermissions,
            ...profile.permissions
              .filter(
                item =>
                  item.product == 'QUALITY' ||
                  item.product == 'TRAINING' ||
                  item.product == 'GEN_AI'
              )
              .map(item => item.name),
          ];
        });

        this.permissionsService.loadPermissions(allPermissions);
        this.setAdminPermission();
        this.setInternalPermission();
        this.setAgentPermission();

        this.organizationService.permissionsLoaded.next(true);

        this.websocketService.initialize();

        this.onboardingStepService.currentOnboardingStep =
          this.organizationService.currentOrganization.steps?.find(
            (step: OnboardingStep) => step.product == 'QUALITY'
          );

        this.firstStepsService.onboardingStatus = {
          importJob: true,
          checklist: true,
          segment: true,
          operation: true,
        };

        if (
          this.firstStepsService.onboardingStatus.importJob &&
          this.firstStepsService.onboardingStatus.checklist &&
          this.firstStepsService.onboardingStatus.segment &&
          this.firstStepsService.onboardingStatus.operation
        ) {
          this.router.navigate([this.userService.returnUrl ?? '/']);
        } else {
          if (!this.onboardingStepService.currentOnboardingStep) {
            this.startOnboardingStep();
            return;
          }

          if (
            this.onboardingStepService.currentOnboardingStep.status !=
            OnboardingStepStatus.COMPLETED
          ) {
            this.firstStepsService.goToFirstSteps();
            return;
          }

          if (this.userService.returnUrl == '/first-steps') {
            this.userService.returnUrl = environment.features.homepage;
          }

          this.router.navigate([this.userService.returnUrl]);
        }

        // this.onboardingService.getStatus().subscribe(data => {

        // }, error => {
        // 	this.toastService.showDanger(SystemMessages.UNEXPECTED_ERROR);
        // });
      },
      error => {
        console.error(error);
        if (
          (error?.error?.error != null || error?.error?.error != undefined) &&
          BusinessException[error?.error?.error] != null
        ) {
          this.toastService.showWarning(BusinessException[error?.error?.error]);
        } else {
          this.toastService.showDanger(SystemMessages.UNEXPECTED_ERROR);
        }
      }
    );
  }

  setAdminPermission() {
    if (
      this.currentUser.organizations.find(
        org => org.id === this.organizationService.currentOrganization.id
      )?.admin
    ) {
      this.permissionsService.addPermission(QualityPermissions.ALL_PERMISSIONS);
    }
  }

  setInternalPermission() {
    if (this.userService.currentUser.type === 'INTERNAL') {
      this.permissionsService.addPermission(QualityPermissions.INTERNAL);
      // this.permissionsService.addPermission(QualityPermissions.ALL_PERMISSIONS);
    }
  }

  setAgentPermission() {
    const currentOrg = this.currentUser.organizations.find(
      org => org.id === this.organizationService.currentOrganization.id
    );
    
    if (
      currentOrg &&
      currentOrg.agentInformation &&
      currentOrg.agentInformation.codes &&
      currentOrg.agentInformation.codes.length > 0
    ) {
      this.permissionsService.addPermission(QualityPermissions.IS_AGENT);
    }
  }

  private startOnboardingStep(): void {
    let onboardingStep = new OnboardingStep();
    onboardingStep.step = 0;
    onboardingStep.name = OnboadingStepNames.FIRST_STEPS;
    onboardingStep.product = Products[Products.QUALITY];
    onboardingStep.status = OnboardingStepStatus.IN_PROGRESS;

    this.onboardingStepService.currentOnboardingStep = onboardingStep;

    this.onboardingStepService.post(onboardingStep).subscribe(
      data => {
        this.firstStepsService.goToFirstSteps();
      },
      error => {
        console.error(error);
        if (
          (error?.error?.error != null || error?.error?.error != undefined) &&
          BusinessException[error?.error?.error] != null
        ) {
          this.toastService.showWarning(BusinessException[error?.error?.error]);
        } else {
          this.toastService.showDanger(SystemMessages.UNEXPECTED_ERROR);
        }
      }
    );
  }

  private onboadFix(): void {
    this.onboardingStepService.currentOnboardingStep.step = 0;
    this.onboardingStepService.currentOnboardingStep.status =
      OnboardingStepStatus.IN_PROGRESS;

    this.onboardingStepService
      .put(this.onboardingStepService.currentOnboardingStep)
      .subscribe(
        data => {},
        error => {
          console.error(error);
          if (
            (error?.error?.error != null || error?.error?.error != undefined) &&
            BusinessException[error?.error?.error] != null
          ) {
            this.toastService.showWarning(BusinessException[error?.error?.error]);
          } else {
            this.toastService.showDanger(SystemMessages.UNEXPECTED_ERROR);
          }
        }
      );
  }
}
